import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/feature/AdminLayout';
import ErrorState from '@/components/base/ErrorState';
import { supabase } from '@/lib/supabaseClient';
import { requireAuth } from '@/middleware/authGuard';
import { getTenantId } from '@/lib/getTenant';
import { uploadFileToVps, deleteFileFromVps } from '@/lib/vpsStorage';
import Swal from 'sweetalert2';

// ── Types ──────────────────────────────────────────────────────────────────────
type TabId = 'meta' | 'og' | 'keywords' | 'sitemap' | 'robots';

interface TabDef {
  id: TabId;
  label: string;
  icon: string;
}

interface SeoData {
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  og_image: string | null;
  canonical_url: string;
}

interface TenantInfo {
  id: string;
  slug: string | null;
  name: string;
}

// ── Tab definitions ────────────────────────────────────────────────────────────
const TABS: TabDef[] = [
  { id: 'meta',     label: 'Meta Tags',   icon: 'ri-code-line' },
  { id: 'og',       label: 'Open Graph',  icon: 'ri-share-line' },
  { id: 'keywords', label: 'Keywords',    icon: 'ri-price-tag-3-line' },
  { id: 'sitemap',  label: 'Sitemap',     icon: 'ri-map-2-line' },
  { id: 'robots',   label: 'Robots',      icon: 'ri-robot-line' },
];

// ── Input styles ───────────────────────────────────────────────────────────────
const INPUT_CLASS = 'w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:ring-2 focus:ring-[#C8A96E]/10 transition-all';

const TEXTAREA_CLASS = 'w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:ring-2 focus:ring-[#C8A96E]/10 transition-all resize-none';

// ── Google Preview ─────────────────────────────────────────────────────────────
function GooglePreview({ title, description, url }: { title: string; description: string; url: string }) {
  const displayTitle = title || 'Your Studio Name | Photography Services';
  const displayDesc = description || 'Professional photography studio. We capture timeless moments with artistic vision.';
  const displayUrl = url || 'yourstudio.com';

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 flex items-center justify-center rounded-md bg-white border border-slate-200">
          <i className="ri-google-line text-slate-500 text-sm"></i>
        </div>
        <p className="text-slate-600 text-xs font-semibold uppercase tracking-wider">Google Search Preview</p>
      </div>
      <div className="max-w-lg">
        <p className="text-slate-400 text-xs mb-1 font-mono truncate">{displayUrl} › photography</p>
        <p className="text-[#1a0dab] text-lg font-medium leading-snug line-clamp-1 hover:underline cursor-pointer">
          {displayTitle}
        </p>
        <p className="text-slate-600 text-sm leading-relaxed mt-1 line-clamp-2">
          {displayDesc}
        </p>
      </div>
      <p className="text-slate-400 text-[11px] mt-3">This is an approximation of how your page may appear in Google results.</p>
    </div>
  );
}

// ── OG Preview ─────────────────────────────────────────────────────────────────
function OgPreview({ title, description, image, url }: { title: string; description: string; image: string | null; url: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden max-w-md">
      <div className="bg-slate-100 h-40 flex items-center justify-center overflow-hidden">
        {image ? (
          <img src={image} alt="OG preview" className="w-full h-full object-cover object-top" />
        ) : (
          <div className="text-center">
            <i className="ri-image-line text-slate-300 text-3xl"></i>
            <p className="text-slate-300 text-xs mt-1">No image set</p>
          </div>
        )}
      </div>
      <div className="p-3">
        <p className="text-slate-400 text-[11px] uppercase tracking-wider mb-0.5">{url || 'yourstudio.com'}</p>
        <p className="text-slate-800 text-sm font-semibold line-clamp-1">{title || 'Your Studio Name'}</p>
        <p className="text-slate-500 text-xs leading-snug mt-0.5 line-clamp-2">{description || 'Professional photography services.'}</p>
      </div>
      <p className="text-slate-400 text-[10px] px-3 pb-2">Facebook / WhatsApp / LinkedIn preview</p>
    </div>
  );
}

// ── Meta Tags Tab ──────────────────────────────────────────────────────────────
function MetaTagsTab({ data, onChange, tenantName, publicUrl }: {
  data: SeoData;
  onChange: (field: keyof SeoData, value: string) => void;
  tenantName: string;
  publicUrl: string;
}) {
  return (
    <div className="space-y-5 max-w-2xl">
      {/* Site Title */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 mb-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-50">
            <i className="ri-heading text-amber-500 text-sm"></i>
          </span>
          <h3 className="text-slate-700 font-semibold text-sm">Site Title</h3>
        </div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-slate-500 text-xs font-medium uppercase tracking-wider">SEO Title</label>
          <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${data.seo_title.length > 55 ? 'bg-amber-100 text-amber-600' : data.seo_title.length > 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
            {data.seo_title.length}/60
          </span>
        </div>
        <input
          type="text"
          value={data.seo_title}
          onChange={(e) => onChange('seo_title', e.target.value)}
          maxLength={60}
          placeholder={`${tenantName} | Wedding Photography & Video`}
          className={INPUT_CLASS}
        />
        <p className="text-slate-400 text-[11px] mt-1.5">Appears in browser tabs and Google search results. Keep it under 60 characters.</p>
      </div>

      {/* Meta Description */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 mb-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50">
            <i className="ri-file-text-line text-emerald-500 text-sm"></i>
          </span>
          <h3 className="text-slate-700 font-semibold text-sm">Meta Description</h3>
        </div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-slate-500 text-xs font-medium uppercase tracking-wider">Description</label>
          <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${data.seo_description.length > 150 ? 'bg-amber-100 text-amber-600' : data.seo_description.length > 0 ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
            {data.seo_description.length}/160
          </span>
        </div>
        <textarea
          value={data.seo_description}
          onChange={(e) => onChange('seo_description', e.target.value)}
          rows={3}
          maxLength={160}
          placeholder="Professional wedding photography studio in your city. We capture timeless moments with artistic vision and cinematic style."
          className={TEXTAREA_CLASS}
        />
        <p className="text-slate-400 text-[11px] mt-1.5">The snippet shown under your URL in Google. Include your city and key services.</p>
      </div>

      {/* Canonical URL */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 mb-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-sky-50">
            <i className="ri-link text-sky-500 text-sm"></i>
          </span>
          <h3 className="text-slate-700 font-semibold text-sm">Canonical URL</h3>
        </div>
        <input
          type="text"
          value={data.canonical_url}
          onChange={(e) => onChange('canonical_url', e.target.value)}
          placeholder={publicUrl || 'https://yourstudio.com'}
          className={INPUT_CLASS}
        />
        <p className="text-slate-400 text-[11px] mt-1.5">Tells search engines which URL is the primary version of your page. Leave empty to use your public URL automatically.</p>
      </div>
    </div>
  );
}

// ── Open Graph Tab ─────────────────────────────────────────────────────────────
function OpenGraphTab({ data, onChange, tenantId, onUploaded }: {
  data: SeoData;
  onChange: (field: keyof SeoData, value: string) => void;
  tenantId: string;
  onUploaded?: (metadata: { fileId?: string; url?: string }) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setUploadError('Only images allowed'); return; }
    if (file.size > 10 * 1024 * 1024) { setUploadError('Max 10MB'); return; }
    setUploading(true);
    setUploadError(null);
    try {
      const result = await uploadFileToVps({
        file,
        tenantId,
        folder: 'seo',
        allowedTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
        maxSizeMB: 10,
      });
      if (!result.success || !result.url) {
        setUploadError(result.error || 'Upload failed');
        Swal.fire({ title: 'Error', text: result.error || 'Upload failed', icon: 'error', confirmButtonColor: '#C8A96E', background: '#ffffff', color: '#0f172a' });
      } else {
        onChange('og_image', result.url);
        onUploaded?.({ fileId: result.fileId, url: result.url });
      }
    } catch {
      setUploadError('Upload failed. Try again.');
      Swal.fire({ title: 'Error', text: 'Upload failed. Try again.', icon: 'error', confirmButtonColor: '#C8A96E', background: '#ffffff', color: '#0f172a' });
    }
    setUploading(false);
  };

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Info */}
      <div className="flex items-start gap-3 bg-[#C8A96E]/8 border border-[#C8A96E]/20 rounded-xl p-4">
        <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C8A96E]/15 flex-shrink-0">
          <i className="ri-share-forward-line text-[#9a7840] text-sm"></i>
        </span>
        <div>
          <p className="text-[#7a5e2a] font-semibold text-sm mb-0.5">Social Media Sharing</p>
          <p className="text-[#9a7840] text-xs leading-relaxed">Control how your page looks when shared on Facebook, WhatsApp, LinkedIn, Twitter, and other platforms.</p>
        </div>
      </div>

      {/* OG Image */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 mb-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-50">
            <i className="ri-image-add-line text-rose-500 text-sm"></i>
          </span>
          <h3 className="text-slate-700 font-semibold text-sm">OG Image</h3>
        </div>
        <p className="text-slate-400 text-[11px] mb-3">Recommended: 1200 × 630 px. This image appears when your page is shared on social media.</p>
        {data.og_image ? (
          <div className="relative rounded-xl overflow-hidden border border-slate-200 group">
            <img src={data.og_image} alt="OG" className="w-full h-48 object-cover object-top" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <button
                onClick={() => onChange('og_image', '')}
                className="flex items-center gap-1.5 bg-white text-red-500 text-xs font-medium px-3 py-2 rounded-lg shadow-sm hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-delete-bin-line"></i>
                Remove
              </button>
            </div>
          </div>
        ) : (
          <label className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 px-6 py-8 transition-colors cursor-pointer ${uploading ? 'bg-slate-50 opacity-60' : 'hover:border-[#C8A96E]/40 hover:bg-[#C8A96E]/5'}`}>
            <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
            <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-100 text-slate-400">
              <i className="ri-upload-cloud-2-line text-lg"></i>
            </span>
            <p className="text-slate-500 text-xs font-medium">{uploading ? 'Uploading...' : 'Upload OG image'}</p>
            <p className="text-slate-300 text-[10px]">PNG or JPG, max 10MB</p>
          </label>
        )}
        {uploadError && <p className="text-red-500 text-xs mt-2">{uploadError}</p>}
      </div>

      {/* OG Title note */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 mb-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-50">
            <i className="ri-information-line text-slate-500 text-sm"></i>
          </span>
          <h3 className="text-slate-700 font-semibold text-sm">OG Title & Description</h3>
        </div>
        <p className="text-slate-500 text-xs leading-relaxed">
          By default, Open Graph uses your <strong>Site Title</strong> and <strong>Meta Description</strong> from the Meta Tags tab. 
          Most platforms (Facebook, WhatsApp, LinkedIn) will pull those values automatically, so separate OG fields are rarely needed.
        </p>
        <p className="text-slate-400 text-[11px] mt-2">
          If you need custom OG text different from your SEO meta tags, contact your developer to add og:title and og:description fields.
        </p>
      </div>
    </div>
  );
}

// ── Keywords Tab ───────────────────────────────────────────────────────────────
function KeywordsTab({ data, onChange }: {
  data: SeoData;
  onChange: (field: keyof SeoData, value: string) => void;
}) {
  const keywords = data.seo_keywords
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean);

  const addKeyword = (kw: string) => {
    const trimmed = kw.trim();
    if (!trimmed || keywords.includes(trimmed)) return;
    const updated = [...keywords, trimmed].join(', ');
    onChange('seo_keywords', updated);
  };

  const removeKeyword = (kw: string) => {
    const updated = keywords.filter((k) => k !== kw).join(', ');
    onChange('seo_keywords', updated);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const input = e.currentTarget;
      addKeyword(input.value);
      input.value = '';
    }
  };

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Keywords input */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 mb-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-violet-50">
            <i className="ri-price-tag-3-line text-violet-500 text-sm"></i>
          </span>
          <h3 className="text-slate-700 font-semibold text-sm">Target Keywords</h3>
        </div>

        <input
          type="text"
          placeholder="Type a keyword and press Enter..."
          onKeyDown={handleKeyDown}
          className={INPUT_CLASS}
        />
        <p className="text-slate-400 text-[11px] mt-1.5">Add 3–5 keywords relevant to your services and location. Press Enter or comma to add.</p>

        {/* Keyword chips */}
        {keywords.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {keywords.map((kw) => (
              <span
                key={kw}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 border border-violet-200 rounded-full text-xs font-medium text-violet-700"
              >
                {kw}
                <button
                  onClick={() => removeKeyword(kw)}
                  className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-violet-200 text-violet-400 hover:text-violet-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-[10px]"></i>
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Raw value for direct editing */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">
            Raw value <span className="text-slate-300 normal-case font-normal">(comma-separated)</span>
          </label>
          <input
            type="text"
            value={data.seo_keywords}
            onChange={(e) => onChange('seo_keywords', e.target.value)}
            placeholder="wedding photographer, portrait session, engagement photos"
            className={INPUT_CLASS}
          />
        </div>
      </div>

      {/* Keyword density tips */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 mb-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-50">
            <i className="ri-lightbulb-line text-amber-500 text-sm"></i>
          </span>
          <h3 className="text-slate-700 font-semibold text-sm">Best Practices</h3>
        </div>
        <ul className="space-y-2 text-xs text-slate-500">
          <li className="flex items-start gap-2">
            <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0"></i>
            Include your city or region for local SEO
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0"></i>
            Use specific service names (e.g. "wedding photography" not just "photography")
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0"></i>
            3–5 keywords is the sweet spot — avoid keyword stuffing
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-close-line text-red-400 mt-0.5 flex-shrink-0"></i>
            Don't repeat the same keyword in different forms
          </li>
        </ul>
      </div>
    </div>
  );
}

// ── Sitemap Tab ────────────────────────────────────────────────────────────────
function SitemapTab({ publicUrl, tenantName }: { publicUrl: string; tenantName: string }) {
  const baseUrl = publicUrl || 'https://yourstudio.com';
  const today = new Date().toISOString().split('T')[0];

  const sitemapXml = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
  <url>
    <loc>${baseUrl}</loc>
    <lastmod>${today}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>1.0</priority>
  </url>
  <url>
    <loc>${baseUrl}/#services</loc>
    <lastmod>${today}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.8</priority>
  </url>
  <url>
    <loc>${baseUrl}/#gallery</loc>
    <lastmod>${today}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.7</priority>
  </url>
  <url>
    <loc>${baseUrl}/#about</loc>
    <lastmod>${today}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.6</priority>
  </url>
  <url>
    <loc>${baseUrl}/#contact</loc>
    <lastmod>${today}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>0.6</priority>
  </url>
</urlset>`;

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Info */}
      <div className="flex items-start gap-3 bg-[#C8A96E]/8 border border-[#C8A96E]/20 rounded-xl p-4">
        <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C8A96E]/15 flex-shrink-0">
          <i className="ri-map-2-line text-[#9a7840] text-sm"></i>
        </span>
        <div>
          <p className="text-[#7a5e2a] font-semibold text-sm mb-0.5">XML Sitemap</p>
          <p className="text-[#9a7840] text-xs leading-relaxed">Sitemaps help search engines discover and index all the important pages on your site. Pixory AI automatically generates a sitemap for your public studio page.</p>
        </div>
      </div>

      {/* Sitemap URL */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 mb-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-50">
            <i className="ri-link text-slate-500 text-sm"></i>
          </span>
          <h3 className="text-slate-700 font-semibold text-sm">Sitemap URL</h3>
        </div>
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
          <code className="text-slate-600 text-xs font-mono flex-1 truncate">{baseUrl}/sitemap.xml</code>
          <button
            onClick={() => navigator.clipboard.writeText(`${baseUrl}/sitemap.xml`)}
            className="flex items-center gap-1 px-2.5 py-1 text-[10px] text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-copy-line"></i>
            Copy
          </button>
        </div>
        <p className="text-slate-400 text-[11px] mt-2">Submit this URL to Google Search Console to help Google find your pages faster.</p>
      </div>

      {/* Sitemap preview */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 mb-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-50">
            <i className="ri-code-view text-slate-500 text-sm"></i>
          </span>
          <h3 className="text-slate-700 font-semibold text-sm">Sitemap Preview</h3>
        </div>
        <pre className="bg-slate-900 text-emerald-400 text-xs p-4 rounded-lg overflow-x-auto font-mono leading-relaxed max-h-80 overflow-y-auto">
          {sitemapXml}
        </pre>
        <div className="flex flex-wrap gap-3 mt-3">
          {[
            { label: 'Homepage', priority: '1.0', freq: 'Weekly' },
            { label: 'Services', priority: '0.8', freq: 'Monthly' },
            { label: 'Gallery', priority: '0.7', freq: 'Monthly' },
            { label: 'About', priority: '0.6', freq: 'Monthly' },
            { label: 'Contact', priority: '0.6', freq: 'Monthly' },
          ].map((page) => (
            <div key={page.label} className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
              <span className="text-xs font-medium text-slate-600">{page.label}</span>
              <span className="text-[10px] text-slate-400">P:{page.priority} · {page.freq}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Robots Tab ─────────────────────────────────────────────────────────────────
function RobotsTab({ publicUrl }: { publicUrl: string }) {
  const baseUrl = publicUrl || 'https://yourstudio.com';
  const [copied, setCopied] = useState(false);

  const robotsTxt = `# robots.txt for ${baseUrl}
# Generated by Pixory AI — Last updated: ${new Date().toISOString().split('T')[0]}

User-agent: *
Allow: /
Disallow: /admin
Disallow: /sa
Disallow: /portal
Disallow: /api

# Sitemap location
Sitemap: ${baseUrl}/sitemap.xml

# Crawl delay (polite crawling)
Crawl-delay: 10`;

  const handleCopy = () => {
    navigator.clipboard.writeText(robotsTxt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Info */}
      <div className="flex items-start gap-3 bg-[#C8A96E]/8 border border-[#C8A96E]/20 rounded-xl p-4">
        <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C8A96E]/15 flex-shrink-0">
          <i className="ri-robot-line text-[#9a7840] text-sm"></i>
        </span>
        <div>
          <p className="text-[#7a5e2a] font-semibold text-sm mb-0.5">Robots.txt</p>
          <p className="text-[#9a7840] text-xs leading-relaxed">The robots.txt file tells search engine crawlers which pages they can and cannot access on your site. Pixory AI generates this automatically.</p>
        </div>
      </div>

      {/* Robots URL */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 mb-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-50">
            <i className="ri-link text-slate-500 text-sm"></i>
          </span>
          <h3 className="text-slate-700 font-semibold text-sm">Robots.txt URL</h3>
        </div>
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
          <code className="text-slate-600 text-xs font-mono flex-1 truncate">{baseUrl}/robots.txt</code>
          <button
            onClick={() => navigator.clipboard.writeText(`${baseUrl}/robots.txt`)}
            className="flex items-center gap-1 px-2.5 py-1 text-[10px] text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-copy-line"></i>
            Copy
          </button>
        </div>
      </div>

      {/* Robots preview */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
          <div className="flex items-center gap-2.5">
            <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-50">
              <i className="ri-code-view text-slate-500 text-sm"></i>
            </span>
            <h3 className="text-slate-700 font-semibold text-sm">Robots.txt Preview</h3>
          </div>
          <button
            onClick={handleCopy}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all cursor-pointer whitespace-nowrap ${copied ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
          >
            <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <pre className="bg-slate-900 text-slate-300 text-xs p-4 rounded-lg overflow-x-auto font-mono leading-relaxed max-h-80 overflow-y-auto">
          {robotsTxt}
        </pre>
      </div>

      {/* Rules summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 mb-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-50">
            <i className="ri-list-check text-slate-500 text-sm"></i>
          </span>
          <h3 className="text-slate-700 font-semibold text-sm">Crawler Rules</h3>
        </div>
        <div className="space-y-2">
          {[
            { path: '/', label: 'Public site', allowed: true },
            { path: '/admin', label: 'Admin panel', allowed: false },
            { path: '/sa', label: 'Super Admin', allowed: false },
            { path: '/portal', label: 'Client portal', allowed: false },
            { path: '/api', label: 'API endpoints', allowed: false },
          ].map((rule) => (
            <div key={rule.path} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-50">
              <div className="flex items-center gap-3">
                <code className="text-xs font-mono text-slate-500">{rule.path}</code>
                <span className="text-xs text-slate-400">{rule.label}</span>
              </div>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${rule.allowed ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'}`}>
                {rule.allowed ? 'Allowed' : 'Disallowed'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── SEO Score ──────────────────────────────────────────────────────────────────
function SeoScore({ data }: { data: SeoData }) {
  const checks = [
    { label: 'Title set', ok: data.seo_title.length > 0 },
    { label: 'Title length (50–60 chars)', ok: data.seo_title.length >= 50 && data.seo_title.length <= 60 },
    { label: 'Description set', ok: data.seo_description.length > 0 },
    { label: 'Description length (120–160 chars)', ok: data.seo_description.length >= 120 && data.seo_description.length <= 160 },
    { label: 'Keywords added', ok: data.seo_keywords.trim().length > 0 },
    { label: 'OG image uploaded', ok: !!data.og_image },
    { label: 'Canonical URL set', ok: data.canonical_url.trim().length > 0 },
  ];
  const passed = checks.filter((c) => c.ok).length;
  const pct = Math.round((passed / checks.length) * 100);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-slate-700 font-semibold text-sm flex items-center gap-2">
          <i className="ri-bar-chart-2-line text-slate-500"></i>
          SEO Score
        </h3>
        <span className={`text-sm font-bold px-3 py-1 rounded-full ${pct >= 80 ? 'bg-emerald-50 text-emerald-600' : pct >= 50 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-500'}`}>
          {pct}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-slate-100 rounded-full mb-4 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${pct >= 80 ? 'bg-emerald-400' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
          style={{ width: `${pct}%` }}
        ></div>
      </div>

      <div className="space-y-1.5">
        {checks.map((check, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <span className={`w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 ${check.ok ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-300'}`}>
              <i className={`text-[10px] ${check.ok ? 'ri-check-line' : 'ri-subtract-line'}`}></i>
            </span>
            <span className={`text-xs ${check.ok ? 'text-slate-600' : 'text-slate-400'}`}>{check.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function AdminSeoPage() {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('meta');
  const [pendingOgFileId, setPendingOgFileId] = useState<string | null>(null);

  const [seoData, setSeoData] = useState<SeoData>({
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    og_image: null,
    canonical_url: '',
  });

  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const isAuth = await requireAuth();
        if (!isAuth) { navigate('/login'); return; }
        const tid = await getTenantId();
        if (!tid) { setLoading(false); setError('Unable to identify tenant. Please log in again.'); return; }
        setTenantId(tid);

        const [tenantRes, seoRes] = await Promise.all([
          supabase.from('saas_tenants').select('id, slug, name').eq('id', tid).maybeSingle(),
          supabase.from('tenant_website_content').select('seo_title, seo_description, seo_keywords, og_image, canonical_url').eq('tenant_id', tid).maybeSingle(),
        ]);

        if (tenantRes.error) {
          console.error('Tenant fetch error:', tenantRes.error);
          setError('Failed to load tenant information. Please try again.');
          setLoading(false);
          return;
        }
        if (seoRes.error) {
          console.error('SEO fetch error:', seoRes.error);
          setError('Failed to load SEO settings. Please try again.');
          setLoading(false);
          return;
        }

        if (tenantRes.data) setTenantInfo(tenantRes.data as TenantInfo);
        if (seoRes.data) {
          setSeoData({
            seo_title: seoRes.data.seo_title ?? '',
            seo_description: seoRes.data.seo_description ?? '',
            seo_keywords: seoRes.data.seo_keywords ?? '',
            og_image: seoRes.data.og_image ?? null,
            canonical_url: seoRes.data.canonical_url ?? '',
          });
        }
        setLoading(false);
      } catch (err) {
        console.error('SEO page init error:', err);
        setError('An unexpected error occurred while loading SEO settings.');
        setLoading(false);
      }
    };
    init();
  }, [navigate]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = useCallback((field: keyof SeoData, value: string) => {
    setSeoData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    const payload = {
      seo_title: seoData.seo_title,
      seo_description: seoData.seo_description,
      seo_keywords: seoData.seo_keywords,
      og_image: seoData.og_image,
      canonical_url: seoData.canonical_url,
      updated_at: new Date().toISOString(),
    };

    try {
      const { data: existing } = await supabase
        .from('tenant_website_content')
        .select('id')
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (existing) {
        const { error: updateErr } = await supabase.from('tenant_website_content').update(payload).eq('tenant_id', tenantId);
        if (updateErr) throw updateErr;
      } else {
        const { error: insertErr } = await supabase.from('tenant_website_content').insert({ ...payload, tenant_id: tenantId, is_published: true });
        if (insertErr) throw insertErr;
      }

      setSaved(true);
      setPendingOgFileId(null);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      console.error('Save error:', err);
      setSaveError(err?.message || 'Failed to save SEO settings. Please try again.');
      // Cleanup uploaded file if DB save failed
      if (pendingOgFileId) {
        await deleteFileFromVps({ fileId: pendingOgFileId, tenantId, silent: true });
        setPendingOgFileId(null);
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Retry handler ─────────────────────────────────────────────────────────
  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    // Re-trigger the effect by forcing a small state change
    const tid = tenantId;
    setTenantId(null);
    setTimeout(() => {
      if (tid) {
        setTenantId(tid);
        setLoading(false);
      } else {
        // Trigger a full reload via navigate
        navigate('/admin/seo');
      }
    }, 50);
  }, [navigate, tenantId]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const publicSlug = tenantInfo?.slug;
  const publicUrl = publicSlug ? `${window.location.origin}/s/${publicSlug}` : `${window.location.origin}`;
  const canonicalDisplay = seoData.canonical_url || publicUrl;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <AdminLayout>
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-4 md:px-8 pt-6 pb-0">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <h1 className="text-slate-800 font-bold text-xl">SEO Manager</h1>
              <p className="text-slate-500 text-sm mt-0.5">Optimize how your studio appears in search engines</p>
            </div>
            <div className="flex items-center gap-3">
              {saveError && (
                <span className="hidden md:flex items-center gap-1.5 text-red-500 text-xs bg-red-50 px-3 py-1.5 rounded-lg">
                  <i className="ri-error-warning-line"></i>
                  {saveError}
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                {saving ? (
                  <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>Saving...</>
                ) : saved ? (
                  <><i className="ri-check-line"></i>Saved!</>
                ) : (
                  <><i className="ri-save-line"></i>Save All Changes</>
                )}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-0 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all cursor-pointer whitespace-nowrap border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'text-[#9a7840] border-[#C8A96E]'
                    : 'text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={tab.icon}></i>
                </span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="bg-slate-50 min-h-screen">
        <div className="px-4 md:px-8 py-6">
          {loading ? (
            <div className="space-y-4 max-w-2xl">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-white rounded-xl border border-slate-200 animate-pulse" />
              ))}
            </div>
          ) : error ? (
            <div className="max-w-2xl">
              <ErrorState
                message={error}
                onRetry={handleRetry}
              />
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left column: form */}
              <div className="flex-1 min-w-0">
                {activeTab === 'meta' && (
                  <MetaTagsTab
                    data={seoData}
                    onChange={handleChange}
                    tenantName={tenantInfo?.name ?? 'Your Studio'}
                    publicUrl={publicUrl}
                  />
                )}
                {activeTab === 'og' && tenantId && (
                  <OpenGraphTab
                    data={seoData}
                    onChange={handleChange}
                    tenantId={tenantId}
                    onUploaded={({ fileId }) => { if (fileId) setPendingOgFileId(fileId); }}
                  />
                )}
                {activeTab === 'keywords' && (
                  <KeywordsTab data={seoData} onChange={handleChange} />
                )}
                {activeTab === 'sitemap' && (
                  <SitemapTab publicUrl={publicUrl} tenantName={tenantInfo?.name ?? 'Your Studio'} />
                )}
                {activeTab === 'robots' && (
                  <RobotsTab publicUrl={publicUrl} />
                )}
              </div>

              {/* Right column: previews + score */}
              <div className="w-full lg:w-80 flex-shrink-0 space-y-5">
                <GooglePreview
                  title={seoData.seo_title}
                  description={seoData.seo_description}
                  url={canonicalDisplay}
                />
                <OgPreview
                  title={seoData.seo_title}
                  description={seoData.seo_description}
                  image={seoData.og_image}
                  url={canonicalDisplay}
                />
                <SeoScore data={seoData} />
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}